"use client";
import dynamic from "next/dynamic";
import Navigation from "@/HomeFeatures/Navigation";
import { useEffect, useState, useCallback } from "react";
import FullWidthCard from "@/app/success-stories/components/FullWidthCard";
import HalfWidthCard from "@/app/success-stories/components/HalfWidthCard";
import SuccessStoryPopup from "@/app/success-stories/components/SuccessStoryPopup";
import {
  portfolioModalInterface,
  PortfolioAllListInterface,
} from "@/lib/Types";
import Loader from "@/components/Loader";
import { getPortfolioDetailsByID } from "@/lib/ApiClient";

const ContactSection = dynamic(() => import("@/HomeFeatures/Contact2"));
export interface SuccessPortfolioProps {
  portfolioArr: PortfolioAllListInterface[];
  listLoading?: boolean;
  hasMore?: boolean;
  loadMore: () => void;
}

export default function Portfolio({
  portfolioArr,
  listLoading = false,
  hasMore,
  loadMore,
}: SuccessPortfolioProps) {
  const [selectedPortfolio, setSelectedPortfolio] = useState(0);
  const [portfolioDetails, setPortfolioDetails] = useState<
    portfolioModalInterface | undefined
  >();
  const [loading, setLoading] = useState(false);

  /**
   * PATTERN CONFIGURATION
   * To get: 1 Full -> 2 Half -> 1 Full -> 2 Half
   * We need 6 items initially (2 sets of 3).
   * Each "Load More" click will add 3 more items (1 set).
   */
  const INITIAL_VISIBLE = 6;
  const LOAD_MORE_STEP = 3;
  const [visibleCount, setVisibleCount] = useState(INITIAL_VISIBLE);

  const fetchSelectedID = useCallback(async () => {
    if (!selectedPortfolio) return;
    try {
      setLoading(true);
      const data = await getPortfolioDetailsByID(selectedPortfolio);
      setPortfolioDetails(data);
    } catch (err: unknown) {
      console.error(
        err instanceof Error ? err.message : "Failed to fetch details",
      );
    } finally {
      setLoading(false);
    }
  }, [selectedPortfolio]);

  useEffect(() => {
    fetchSelectedID();
  }, [fetchSelectedID]);

  const handleLoadMoreClick = () => {
    const nextCount = visibleCount + LOAD_MORE_STEP;
    setVisibleCount(nextCount);

    // Trigger API fetch if we are reaching the end of the currently loaded array
    if (nextCount >= portfolioArr.length && hasMore && !listLoading) {
      loadMore();
    }
  };

  const displayedProjects = portfolioArr.slice(0, visibleCount);

  return (
    <>
      {selectedPortfolio !== 0 && (
        <SuccessStoryPopup
          onClose={() => setSelectedPortfolio(0)}
          portfolioDetails={portfolioDetails}
          loading={loading}
        />
      )}

      <section className="relative h-[max-content] flex flex-col max-w-[100vw] overflow-hidden">
        <div className="relative z-10 w-full">
          <div className="w-full h-full bg-white bg-cover bg-center flex flex-col">
            <Navigation dark={false} />
            <div className="w-full m-auto xl:mt-[80px] mt-[30px]">
              <h5 className="self-stretch text-center text-[#636363] text-[16px] xl:text-[clamp(0.9375rem,0.4664rem+0.9815vw,2rem)] font-normal tracking-tight">
                / Our Portfolio - Breakthrough Projects /
              </h5>
              <h1 className="self-stretch mx-auto text-center max-w-[23ch] text-black py-[20px] text-[length:var(--service-banner-title)] font-normal clash-font capitalize leading-[100%]">
                We work to{" "}
                <span className="text-[#225BD4]">innovate & are proud</span>&nbsp;of
                what we&apos;ve created.
              </h1>
              <p className="self-stretch text-center text-[#636363] text-[20px] xl:text-[clamp(0.9375rem,0.4664rem+0.9815vw,2rem)] font-normal tracking-tight">
                You’ll love to see some of our best projects. Take a Look!
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="xl:py-[150px] py-[30px]">
        <div className="max-w-[2500px] mx-auto w-[calc(100%_-_10%)]">
          {listLoading && portfolioArr.length === 0 ? (
            <div className="flex justify-center items-center w-full py-10">
              <Loader size={80} />
            </div>
          ) : (
            <>
              {/* Grid Layout */}
              <div className="grid grid-cols-1 2xl:grid-cols-2 gap-[60px]">
                {displayedProjects?.map((project: any, index) =>
                  project.colspan === 2 ? (
                    <FullWidthCard
                      key={`${project.id}-${index}`}
                      project={project}
                      theme="light"
                      imagePriority={index === 0}
                      onClick={() => setSelectedPortfolio(project.id)}
                    />
                  ) : (
                    <HalfWidthCard
                      key={`${project.id}-${index}`}
                      project={project}
                      theme="light"
                      onClick={() => setSelectedPortfolio(project.id)}
                    />
                  ),
                )}
              </div>

              {/* Load More Button */}
              {(hasMore || visibleCount < portfolioArr.length) && (
                <div className="flex flex-col justify-center items-center w-full mt-24 gap-8">
                  {listLoading && <Loader size={40} />}

                  <button
                    onClick={handleLoadMoreClick}
                    disabled={listLoading}
                    aria-label="Load more"
                    style={{ boxShadow: "0px 4px 30px 0px #0000001A" }} // Applied specific shadow
                    className="
                    px-16 py-6 
                    bg-white 
                    text-black 
                    text-[18px] 
                    font-semibold 
                    capitalize 
                    leading-none 
                    text-center 
                    align-middle 
                    rounded-full 
                    transition-all 
                    duration-300 
                    hover:scale-105 
                    hover:bg-gray-90
                    disabled:opacity-50
                  "
                  >
                    {listLoading ? "Loading..." : "View More Projects"}
                  </button>
                </div>
              )}
            </>
          )}

          {!listLoading && portfolioArr.length === 0 && (
            <div className="flex justify-center items-center w-full py-10 text-gray-500">
              <p>No Portfolios Found.</p>
            </div>
          )}
        </div>
      </section>

      <ContactSection />
    </>
  );
}
